import * as React from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { Back } from 'modules/core/components/Card/types'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import AutomationsShow from 'modules/automations/components/Show'
import { Store } from 'modules/automations/lib/Store'
import { useAutomationContext } from 'modules/automations/lib/automationContext'
import { accessGroups } from 'modules/automations/store/collections/accessGroups'
import { actions } from 'modules/automations/store/collections/actions'
import { automations } from 'modules/automations/store/collections/automations'
import type { Automation } from 'modules/automations/store/models/Automation'
import type { ITriggerWithConditions } from 'modules/automations/store/models/Trigger'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { ActivationModal } from './ActivationModal'

interface AutomationDetailsProps {
  automation: Automation
  trigger?: ITriggerWithConditions
  back: Back
  contractVersions: ContractVersions
}

// FIXME: replace the renderButtons with CardHeader.RightAction
interface ActionsProps {
  automation: Automation | null | undefined
  automationsStore: Store
  canEditAutomations: boolean
}

const Actions = ({ automation, automationsStore, canEditAutomations }: ActionsProps) => {
  const [activationModal, setActivationModal] = useState(false)

  if (!automation || !canEditAutomations) return null

  const saveChanges = () => {
    automationsStore.save().then(() => {
      if (automation.isEnabled) {
        return app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
      setActivationModal(true)
    })
  }

  return (
    <>
      <Box paddingRight="s8">
        <Button onClick={() => saveChanges()} type="secondary">
          {i18n.t('automations.show.details.header.buttons.save_changes')}
        </Button>
      </Box>

      {activationModal && (
        <ActivationModal automation={automation} setActivationModal={setActivationModal} />
      )}
    </>
  )
}

export const AutomationDetails = observer(
  ({
    automation,
    trigger,
    back,
    children,
    contractVersions,
  }: React.PropsWithChildren<AutomationDetailsProps>) => {
    const automationsStore = React.useRef(new Store(automation, trigger?.conditions || [])).current
    React.useEffect(() => () => automationsStore.dispose(), [automationsStore])

    const { canEditAutomations } = useAutomationContext()
    const { navigation } = useNavigation()
    const navigate = useNavigate()

    const duplicate = async () => {
      const { value, success } = await asyncAction(() => automation.rpc('duplicate'))

      if (success) {
        // Update the collections related to the automation
        await Promise.all([accessGroups.fetch(), actions.fetch(), automations.fetch()])

        navigate(navigation.settings.automations.id.fullPath({ id: value.id }))
      }
    }

    const backLink = {
      href: back.href,
      label: back.label || '',
    }

    return (
      <ResponsivePage.Body backLink={backLink} showHeader={false}>
        <Box width="full">
          <Box alignItems="flexEnd" flexDirection="rowReverse" gap="s8">
            <Actions
              automationsStore={automationsStore}
              automation={automation}
              canEditAutomations={canEditAutomations}
            />
          </Box>
        </Box>
        {automation.isDefault && canEditAutomations && (
          <Box
            alignItems="center"
            flexDirection="row"
            justifyContent="spaceBetween"
            width="full"
            paddingY="s16"
            paddingX="s24"
            background="grey200"
            border={{
              bottom: {
                style: 'solid',
                width: 's1',
                color: 'grey400',
              },
            }}
          >
            <Text.Normal size="200">
              {i18n.t('automations.show.details.default_banner')}
            </Text.Normal>
            <Button onClick={() => duplicate()}>
              {i18n.t('automations.show.details.duplicate')}
            </Button>
          </Box>
        )}
        <div>
          <AutomationsShow
            automation={automation}
            trigger={trigger}
            automationsStore={automationsStore}
            contractVersions={contractVersions}
          />
        </div>
        {children}
      </ResponsivePage.Body>
    )
  }
)
